/* eslint-disable react/button-has-type */
import PropTypes from 'prop-types';
import { createRef, useEffect, useState } from 'react';
import { Button, Input } from '@ui-propulsion/react-components';
import parse from 'html-react-parser';
import http from 'lc-http';
import styles from './prequalPL.module.scss';
import CurrencyInput from '../../currencyInput/currencyInput';
import Dropdown from '../../dropdown/dropdown';
import * as defaults from '../constants';
import { getNumberFromString } from '../../currencyInput/helpers';
import EmailCapture from '../../emailCapture/emailCapture';
import { ctaTheme } from '../../../utils/util';

function PrequalPL({
  url = defaults.url,
  inputPlaceholder = defaults.inputPlaceholder,
  loanPurposes = defaults.personalLoans,
  tooltip = defaults.tooltip,
  mailOffer = defaults.mailOffer,
  ctaLabel = defaults.ctaLabel,
  ctaThemeProp = '',
  prequalType,
  theme = null,
  popupHead,
  popupBody,
  popupFooter,
  emailConfirmationHead,
  emailConfirmationBody,
  queryParams,
  defaultMaxAmount,
}) {
  const [isVisible, setIsVisible] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [emailState, setEmailState] = useState('');
  const input = createRef();
  const dropdown = createRef();
  const email = createRef();
  const [hasCurrencyError, setHasCurrencyError] = useState('');
  const [hasDropdownError, setHasDropdownError] = useState('');
  const [hasEmailError, setHasEmailError] = useState('');
  const [ctaThemes, setCtaThemes] = useState(styles.redSolid);

  useEffect(() => {
    setCtaThemes(ctaTheme(ctaThemeProp));
  }, [ctaThemeProp]);

  let hasLoanAmount = false;
  let hasLoanPurpose = false;
  let hasEmail = false;
  let minAmount = 1000;
  let maxAmount = defaultMaxAmount || 40000;
  switch (prequalType) {
    case 'Amount Only':
      hasLoanAmount = true;
      break;
    case 'Purpose Only':
      hasLoanPurpose = true;
      break;
    case 'Both amount and purpose':
      hasLoanAmount = true;
      hasLoanPurpose = true;
      break;
    case 'Business':
      hasLoanAmount = true;
      hasLoanPurpose = true;
      hasEmail = true;
      minAmount = 5000;
      maxAmount = defaultMaxAmount || 50000;
      break;
    case 'Auto Finance':
      hasLoanPurpose = true;
      break;
    default:
      hasLoanAmount = false;
      hasLoanPurpose = false;
  }

  const handleBusinessSubmit = (data) => {
    http
      .post('/api/business-send-lead', data)
      .then((response) => {
        let nextPage = response?.data?.nextPage;
        if (nextPage) {
          const urlParams = new URLSearchParams(window?.location?.search);
          nextPage +=
            nextPage.indexOf('?') === -1
              ? `${urlParams.toString() ? `?${urlParams.toString()}` : ''}`
              : `${urlParams.toString() ? `&${urlParams.toString()}` : ''}`;
          window.location.assign(nextPage);
        }
      })
      .catch(() => {});
  };

  const handleSubmit = () => {
    let hasError = false;
    const loanAmount = hasLoanAmount
      ? getNumberFromString(input.current.value)
      : '';
    const urlParams = new URLSearchParams(window?.location?.search).toString();
    const queryParam = urlParams
      ? `${urlParams}`
      : `${queryParams ? `${queryParams}` : ''}`;
    if (hasLoanAmount) {
      if (
        loanAmount === '' ||
        loanAmount > maxAmount ||
        loanAmount < minAmount
      ) {
        setHasCurrencyError(
          `Enter $${minAmount.toLocaleString()} to $${maxAmount.toLocaleString()}`
        );
        hasError = true;
      } else {
        setHasCurrencyError('');
      }
    }
    const loanPurpose = hasLoanPurpose ? dropdown.current.value : '';
    const loanUrl = loanPurposes.find((item) => item.value === loanPurpose);
    const redUrl = loanUrl?.url ?? url;
    const stateData = loanPurposes.find(
      (option) => option.value === loanPurpose
    );
    if (hasLoanPurpose) {
      if (loanPurpose === '') {
        if (prequalType === 'Auto Finance') {
          setHasDropdownError('Please select a state');
        } else {
          setHasDropdownError('Select a loan purpose');
        }
        hasError = true;
      } else {
        setHasDropdownError('');
      }
    }
    const emailId = hasEmail ? email.current.value : '';
    if (hasEmail) {
      if (
        emailId === '' ||
        !emailId.match(/^[A-Za-z0-9._%+-]+@(?:[A-Za-z0-9-]+\.)+[A-Za-z]{2,4}$/)
      ) {
        setHasEmailError('Enter a valid email address');
        hasError = true;
      } else {
        setHasEmailError('');
      }
    }

    if (!hasError) {
      if (prequalType === 'Business') {
        handleBusinessSubmit({
          email: emailId,
          amount: loanAmount,
          purpose: loanPurpose,
        });
      } else if (prequalType === 'Auto Finance') {
        if (stateData.emailEnquiry) {
          setIsVisible(!isVisible);
          setEmailState(loanPurpose);
        } else {
          window.location.assign(`${url}?state=${loanPurpose}${queryParam}`);
        }
      } else {
        const params = [];
        if (loanAmount) {
          params.push(`loanAmount=${loanAmount}`);
        }
        if (loanPurpose) {
          params.push(`loanPurpose=${loanPurpose}`);
        }

        if (queryParam) params.push(queryParam);

        window.location.assign(`${redUrl}?${params.join('&')}`);
      }
    }
  };
  const hideEmailForm = () => {
    setIsVisible(!isVisible);
  };
  const handleNewsLetterSuccess = () => {
    setShowConfirmation(true);
  };
  return isVisible ? (
    <div className={styles.emailContainer}>
      <div className={styles.emailForm}>
        {showConfirmation ? (
          <>
            {emailConfirmationHead && <h2>{emailConfirmationHead}</h2>}
            <button
              className={styles.closeButton}
              aria-label="Close"
              onClick={hideEmailForm}
            />
            <div>{parse(emailConfirmationBody)}</div>
            <Button
              className="Prequal__CTA"
              color="red"
              href="/personal-loan"
              type="submit"
            >
              Check Your Rate
            </Button>
          </>
        ) : (
          <>
            {popupHead && <h2>{popupHead}</h2>}
            <button
              className={styles.closeButton}
              aria-label="Close"
              onClick={hideEmailForm}
            />
            <div>{parse(`${popupBody}`)}</div>
            <EmailCapture
              successCallback={handleNewsLetterSuccess}
              buttonText="Submit"
              type="email sender"
              state={emailState}
            />
            {popupFooter && <span>{popupFooter}</span>}
          </>
        )}
      </div>
    </div>
  ) : (
    <div className={styles.wrapper}>
      {hasLoanAmount && (
        <CurrencyInput
          ref={input}
          hasError={hasCurrencyError}
          className={
            hasCurrencyError
              ? `${styles.currencyInputError} ${styles.currencyInput}`
              : styles.currencyInput
          }
          placeholder={inputPlaceholder}
          data-testid="loan-amount"
        />
      )}
      {hasEmail && (
        <div>
          {hasEmailError && (
            <span error-span="email-error" className={styles.errorText}>
              {hasEmailError}
            </span>
          )}
          <Input
            inputRef={email}
            className={
              hasEmailError
                ? `${styles.emailError} ${styles.emailInput}`
                : styles.emailInput
            }
            data-testid="email"
            placeholder="Email Address"
          />
        </div>
      )}
      {hasLoanPurpose && (
        <Dropdown
          ref={dropdown}
          options={loanPurposes}
          errorMessage={hasDropdownError}
          className={
            hasDropdownError
              ? `${styles.dropdownError} ${styles.dropdown}`
              : styles.dropdown
          }
          elevation
          data-testid="loan-purpose"
        />
      )}
      <div className={styles.cta}>
        <div className={styles.ctaWrapper}>
          <div className={styles.prequalCta}>
            <Button
              type="submit"
              variant="tertiary"
              fullWidth
              data-testid="check-rate"
              className={`${styles.submitBtn} ${ctaThemes}`}
              aria-label="submit-rate"
              onClick={handleSubmit}
            >
              {ctaLabel}
            </Button>
          </div>
          {tooltip.note && (
            <div className={styles.footer}>
              <span
                className={`${
                  theme === 'white'
                    ? styles.tooltipNotedark
                    : styles.tooltipNotewhite
                }`}
              >
                {parse(tooltip.note)}
              </span>
            </div>
          )}
        </div>
        {mailOffer && (
          <div className={styles.mailOffer}>
            <a href={mailOffer.path}>
              <span
                className={`${
                  theme === 'white'
                    ? styles.spanLabeldark
                    : styles.spanLabelwhite
                } `}
              >
                {mailOffer.label}
              </span>
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

PrequalPL.propTypes = {
  url: PropTypes.string,
  inputPlaceholder: PropTypes.string,
  loanPurposes: PropTypes.arrayOf(PropTypes.object),
  tooltip: PropTypes.shape({
    note: PropTypes.string,
    helpNote: PropTypes.string,
  }),
  mailOffer: PropTypes.shape({
    path: PropTypes.string,
    label: PropTypes.string,
  }),
  ctaLabel: PropTypes.string,
  prequalType: PropTypes.string,
  theme: PropTypes.string,
  popupHead: PropTypes.string,
  popupBody: PropTypes.string,
  popupFooter: PropTypes.string,
  emailConfirmationHead: PropTypes.string,
  emailConfirmationBody: PropTypes.string,
};

export default PrequalPL;
